import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import WhatsAppIcon from "./WhatsAppIcon";
import { NAV_LINKS } from "@/lib/constants";
import { useSite } from "@/context/SiteContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote } = useSite();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 border-b transition-all duration-300 ${
        scrolled ? "border-white/10 bg-ink-950/85 backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="container-brox flex h-16 items-center justify-between gap-4 sm:h-20">
        <Link to="/" aria-label="BROX CONSTRUCT — acasă" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 xl:flex" aria-label="Navigare principală">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-200 hover:text-copper ${
                  isActive ? "text-copper" : "text-paper/75"
                }`
              }
              data-testid={`nav-link-${link.to.replace(/\//g, "").replace(/-/g, "-") || "acasa"}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openQuote({ source: "nav" })}
            className="hidden items-center gap-2.5 bg-copper px-6 py-3 font-display text-base font-bold uppercase tracking-wider text-ink-950 transition-all duration-300 hover:bg-copper-dark sm:inline-flex min-h-[44px]"
            data-testid="nav-cta-quote"
          >
            <WhatsAppIcon className="h-4 w-4" />
            CERE OFERTĂ
          </button>
          <button
            className="inline-flex h-11 w-11 items-center justify-center border border-white/15 text-paper transition-colors hover:border-copper hover:text-copper xl:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Închide meniul" : "Deschide meniul"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-ink-950/95 backdrop-blur-xl xl:hidden"
            aria-label="Meniu mobil"
            data-testid="nav-mobile-menu"
          >
            <div className="container-brox flex flex-col py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `flex items-center justify-between border-b border-white/5 py-4 font-display text-2xl font-bold uppercase tracking-wide ${
                        isActive ? "text-copper" : "text-paper"
                      }`
                    }
                    data-testid={`nav-mobile-link-${link.to.replace(/\//g, "").replace(/-/g, "-") || "acasa"}`}
                  >
                    {link.label}
                    <span className="font-mono text-xs text-copper">0{i + 1}</span>
                  </NavLink>
                </motion.div>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  openQuote({ source: "mobile-menu" });
                }}
                className="btn-primary mt-6 w-full"
                data-testid="nav-mobile-cta-quote"
              >
                <WhatsAppIcon className="h-5 w-5" />
                CERE OFERTĂ PE WHATSAPP
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
